import { TagType } from 'utils/types/enums';
import {
	CallLog,
	InboundCall,
	IntakeForm,
	IntakeFormWithInitialVitals,
	NurseAssignResponse,
	TRIAGE_LEVELS,
	VetAssignResponse,
	VetDashState,
} from '../utils/dataTypes';
import { pimsApi } from './pimsApi';

// Define a service using a base URL and expected endpoints
export const vetDashApi = pimsApi.injectEndpoints({
	endpoints: (builder) => ({
		getPatients: builder.query<VetDashState, null>({
			query: (searchQuery) => `dashboard`,
			providesTags: (result, error, id) => [
				{ type: 'Visit', id: 'LIST' },
			],
			transformResponse: (response: { data: VetDashState }) => response.data,
		}),
		assignSelfToPatient: builder.mutation<
			{ data: VetAssignResponse },
			{ visitId: number }
		>({
			query({ visitId }) {
				return {
					url: `visit/${visitId}/encounter?op=assign`,
					method: 'PUT',
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: 'Visit', id: 'LIST' },
				{ type: 'Visit', id: visitId },
				{ type: TagType.Encounter, id: visitId },
			],
		}),
		assignUserToPatient: builder.mutation<
			{ data: VetAssignResponse },
			{ visitId: number, userId: number }
		>({
			query({ visitId, userId }) {
				return {
					url: `visit/${visitId}/encounter/${userId}`,
					method: 'PUT',
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: 'Visit', id: 'LIST' },
				{ type: 'Visit', id: visitId },
				{ type: TagType.Encounter, id: visitId },
			],
		}),
		unassignDoctorFromPatient: builder.mutation<
			{ data: VetAssignResponse },
			{ visitId: number }
		>({
			query({ visitId }) {
				return {
					url: `visit/${visitId}/encounter?op=dismiss`,
					method: 'PUT',
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: 'Visit', id: 'LIST' },
				{ type: 'Visit', id: visitId },
				{ type: TagType.Encounter, id: visitId },
			],
		}),
		assignSelfToPatientAsNurse: builder.mutation<
			{ data: NurseAssignResponse },
			{ visitId: number }
		>({
			query({ visitId }) {
				return {
					url: `visit/${visitId}/encounter_nurse?op=assign`,
					method: 'PUT',
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: 'Visit', id: 'LIST' },
				{ type: 'Visit', id: visitId },
				{ type: TagType.EncounterNurse, id: visitId },
			],
		}),
		unassignNurseFromPatient: builder.mutation<
			{ data: NurseAssignResponse },
			{ visitId: number }
		>({
			query({ visitId }) {
				return {
					url: `visit/${visitId}/encounter_nurse?op=dismiss`,
					method: 'PUT',
				};
			},
			invalidatesTags: (result, error, { visitId }) => [
				{ type: 'Visit', id: 'LIST' },
				{ type: 'Visit', id: visitId },
				{ type: TagType.EncounterNurse, id: visitId },
			],
		}),
		getInboundCalls: builder.query<InboundCall[], null>({
			query: () => `inbound-call`,
			providesTags: (result, error, id) => [
				{ type: TagType.InboundCall, id: 'LIST' }
			],
			transformResponse: (response: { data: InboundCall[] }) => response.data
		}),
		getInboundCall: builder.query<InboundCall, { sessionId: string }>({
			query({ sessionId }) {
				return {
					url: `inbound-call/${sessionId}`
				}
			},
			providesTags: (result, error, { sessionId }) => [
				{ type: TagType.InboundCall, id: sessionId }
			]
		}),
		addCallLog: builder.mutation<CallLog, { sessionId: string, log: CallLog }>({
			query({ sessionId, log }) {
				return {
					url: `call-log/${sessionId}`,
					method: 'POST',
					body: log
				}
			},
			transformResponse: (response: { data: CallLog }) => response.data,
			transformErrorResponse: (response: any) => response.data,
			invalidatesTags: (result, error, { sessionId }) => [
				{ type: TagType.InboundCall, id: sessionId },
				{ type: TagType.InboundCall, id: 'LIST' }
			]
		}),
		modifyCallLog: builder.mutation<CallLog, { sessionId: string, log: CallLog }>({
			query({ sessionId, log }) {
				return {
					url: `call-log/${sessionId}`,
					method: 'PUT',
					body: log
				}
			},
			transformResponse: (response: { data: CallLog }) => response.data,
			transformErrorResponse: (response: any) => response.data,
			invalidatesTags: (result, error, { sessionId }) => [
				{ type: TagType.InboundCall, id: sessionId },
				{ type: TagType.InboundCall, id: 'LIST' }
			]
		}),
		setSpiked: builder.mutation<void, number>({
			query: (visitId: number) => ({
				url: `/visit/${visitId}/set_spiked`,
				method: 'PUT'
			}),
			invalidatesTags: (result, error, visitId) => [
				{ type: 'Visit', id: 'LIST' },
				{ type: TagType.Visit, visitId }
			]
		}),
		removeSpiked: builder.mutation<void, number>({
			query: (visitId: number) => ({
				url: `/visit/${visitId}/remove_spiked`,
				method: 'PUT'
			}),
			invalidatesTags: (result, error, visitId) => [
				{ type: 'Visit', id: 'LIST' },
				{ type: TagType.Visit, visitId }
			]
		}),
		modifyVisitIntake: builder.mutation<any, { visit_id: number, intakeForm: IntakeForm }>({
			query({ visit_id, intakeForm }) {
				return {
					url: `visit/${visit_id}/intake`,
					method: 'PUT',
					body: intakeForm
				}
			},
			transformResponse: (_, meta) => {
				return { status: meta?.response?.status }
			},
			transformErrorResponse: (response: any) => response.data,
			invalidatesTags: (result, error, visitId) => [
				{ type: TagType.Visit, visitId }
			]
		}),
		modifyVisitIntakeWithInitialVitals: builder.mutation<any, { visit_id: number, intakeForm: IntakeFormWithInitialVitals }>({
			query({ visit_id, intakeForm }) {
				return {
					url: `visit/${visit_id}/intake_with_initial_vitals`,
					method: 'POST',
					body: intakeForm
				}
			},
			transformResponse: (_, meta) => {
				return { status: meta?.response?.status }
			},
			transformErrorResponse: (response: any) => response.data,
			invalidatesTags: (result, error, visitId) => [
				{ type: TagType.Visit, visitId }
			]
		}),
	}),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints

export const {
	useGetPatientsQuery,
	useAssignSelfToPatientMutation,
	useAssignUserToPatientMutation,
	useAssignSelfToPatientAsNurseMutation,
	useUnassignDoctorFromPatientMutation,
	useUnassignNurseFromPatientMutation,
	useGetInboundCallsQuery,
	useAddCallLogMutation,
	useModifyCallLogMutation,
	useSetSpikedMutation,
	useRemoveSpikedMutation,
	useModifyVisitIntakeMutation,
	useModifyVisitIntakeWithInitialVitalsMutation,
} = vetDashApi;
